import "../utils/axiosConfig"; // runs the 401/403 logout logic globally
import React, { useState, useEffect, useRef, useMemo } from "react";
import "../Style/EventCalendar.css";
import {
  buildGoogleCalendarUrl,
  buildOutlookCalendarUrl,
  buildYahooCalendarUrl,
  downloadICS,
  eventHasValidDates,
  getEventStatus,
} from "../utils/calendarUtils";
import ventureSummitGiraffe from "../Images/KPMG Africa Private Enterprise Venture Summit.png";
import summit2025Img from "../Images/2025 - KPMG Private Enterprise Venture Summit.png";
import peConnectSummit2024Img from "../Images/private enterprise-connect-summit-2024.png";
import gti2024AfricaImg from "../Images/Global Tech Innovator Final 2024 - Africa.png";
import gti2025EAImg from "../Images/2025-KPMG Private Enterprise Tech Innovator in East Africa.png";

// Sample events data. Dates accept either ISO ("YYYY-MM-DD") or friendly
// ("18th May 2026") formats. Add `startTime` / `endTime` fields for timed events.
const eventsData = [
  {
    id: 1,
    title: "2026 Private Enterprise Africa Venture Summit",
    description:
      "After two successful editions, KPMG Private Enterprise Africa hosts the third edition of its transformative summit. Africa Accelerated — powering Africa's engines of growth: an immersive, forward-thinking experience for diverse stakeholders to shape the conversation on the continent's future.",
    location: "JW Marriott Hotel, Nairobi, Kenya",
    start: "18th May 2026",
    end: "22nd May 2026",
    registrationUrl:
      "https://kpmg.com/ke/en/insights/2026/05/2026-kpmg-africa-private-enterprise-venture-summit.html",
    images: [ventureSummitGiraffe],
  },
  {
    id: 2,
    title: "KPMG Private Enterprise Tech Innovator 2025 — East Africa",
    description:
      "Now in its fifth year, the Global Tech Innovator competition brings together the brightest minds in technology innovation — challenging the status quo and opening a window to the future. The 2025 cycle includes a dedicated East Africa heat highlighting fast-growing startups with bold tech-enabled solutions ready to scale internationally. Applications opened in January 2025; local heats ran May–August 2025; East Africa winners qualified to pitch at the Global Final in November 2025, where Rhinoflux (Japan) was named global winner. Eligibility: startups operating ≤7 years, with revenue between US$1M–$15M or having raised at least US$500K in equity. East Africa coverage: Kenya, Uganda, Tanzania, Rwanda, Ethiopia.",
    location: "East Africa",
    start: null,
    end: null,
    registrationUrl:
      "https://kpmg.com/ke/en/home/insights/2025/06/global_tech_innovator_2025.html",
    images: [gti2025EAImg],
  },
  {
    id: 3,
    title: "KPMG Private Enterprise Venture Summit 2025",
    description:
      "The second edition of KPMG Private Enterprise's transformative summit brought together African and global trailblazers redefining private capital, innovation and entrepreneurship. Founders, investors and strategic partners engaged through panel discussions, interactive thematic sessions, deal rooms and networking — across four tracks: Family Office & Private Clients Festival, Investor Festival, Impact Investing Festival, and Ecosystem Festival.",
    location: "Nairobi, Kenya",
    start: "27th May 2025",
    end: "30th May 2025",
    registrationUrl:
      "https://kpmg.com/ke/en/home/insights/2025/05/2025_private_enterprise_venture_summit.html",
    images: [summit2025Img],
  },
  {
    id: 4,
    title: "Global Tech Innovator Final 2024 — Africa",
    description:
      "The 2024 KPMG Private Enterprise Global Tech Innovator final celebrated Africa's leading tech entrepreneurs as they competed on the global stage.",
    location: "Africa",
    start: "13th Nov 2024",
    end: "13th Nov 2024",
    registrationUrl:
      "https://kpmg.com/ke/en/home/insights/2025/06/global_tech_innovator_2025.html",
    images: [gti2024AfricaImg],
  },
  {
    id: 6,
    title: "KPMG Private Enterprise Connect Summit 2024",
    description:
      "The inaugural KPMG Private Enterprise Africa Summit — connecting founders, investors and ecosystem partners across the continent through panel discussions, deal rooms and networking sessions.",
    location: "Nairobi, Kenya",
    start: "21st May 2024",
    end: "23rd May 2024",
    registrationUrl:
      "https://kpmg.com/ke/en/home/insights.html",
    images: [peConnectSummit2024Img],
  },
  {
    id: 5,
    title: "Female Founders Competition in Africa",
    description:
      "An opportunity to showcase innovative ideas and access the resources to grow. Application deadline was 31 January 2025.",
    location: "Nairobi, Kenya",
    start: null,
    end: null,
    registrationUrl: "http://bit.ly/4gOsTKA",
    images: [
      "https://media.licdn.com/dms/image/v2/D4D22AQEvUxYPRySluQ/feedshare-shrink_800/B4DZSYe8xJHIAg-/0/1737725041570?e=1757548800&v=beta&t=RxvMV60qKiY8t3SgKsMg_qlPFR3UHuMf49nTCWek350",
    ],
  },
];

const STATUS_LABEL = {
  upcoming: "Upcoming",
  ongoing: "Happening now",
  past: "Past event",
  unknown: "Date TBC",
};

const EventPage = () => {
  const [filter, setFilter] = useState("all"); // all | upcoming | past
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const imagesScrollRef = useRef(null);

  // Annotate events with status and pre-built calendar URLs
  const annotated = useMemo(
    () =>
      eventsData.map((e) => {
        const status = getEventStatus(e);
        return {
          ...e,
          status,
          hasValidDates: eventHasValidDates(e),
          googleUrl: buildGoogleCalendarUrl(e),
          outlookUrl: buildOutlookCalendarUrl(e),
          yahooUrl: buildYahooCalendarUrl(e),
        };
      }),
    []
  );

  const filtered = useMemo(() => {
    if (filter === "upcoming") {
      return annotated.filter(
        (e) => e.status === "upcoming" || e.status === "ongoing"
      );
    }
    if (filter === "past") {
      return annotated.filter((e) => e.status === "past");
    }
    return annotated;
  }, [annotated, filter]);

  // Reset index when filter changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [filter]);

  const currentEvent = filtered[currentIndex];

  const checkScrollPosition = () => {
    const c = imagesScrollRef.current;
    if (!c) return;
    setShowLeftArrow(c.scrollLeft > 0);
    setShowRightArrow(c.scrollLeft + c.clientWidth < c.scrollWidth - 1);
  };

  const scrollImages = (direction) => {
    const c = imagesScrollRef.current;
    if (!c) return;
    const amount = c.clientWidth * 0.8;
    c.scrollBy({ left: direction === "right" ? amount : -amount, behavior: "smooth" });
  };

  useEffect(() => {
    if (imagesScrollRef.current) {
      imagesScrollRef.current.scrollLeft = 0;
      checkScrollPosition();
    }
  }, [currentIndex]);

  const handlePrev = () => {
    if (filtered.length === 0) return;
    setCurrentIndex((i) => (i === 0 ? filtered.length - 1 : i - 1));
  };
  const handleNext = () => {
    if (filtered.length === 0) return;
    setCurrentIndex((i) => (i === filtered.length - 1 ? 0 : i + 1));
  };

  if (!currentEvent) {
    return (
      <div className="event-calendar-container">
        <FilterTabs filter={filter} setFilter={setFilter} />
        <div className="event-empty">No events to show in this view.</div>
      </div>
    );
  }

  const { status } = currentEvent;
  const isPast = status === "past";

  return (
    <div className="event-calendar-container">
      <FilterTabs filter={filter} setFilter={setFilter} />

      <div className="event-slider">
        <button
          className="slider-btn prev"
          onClick={handlePrev}
          aria-label="Previous event"
          disabled={filtered.length <= 1}
        >
          ❮
        </button>

        <div className={`event-card ${isPast ? "is-past" : ""}`}>
          <div className="event-card-header">
            <span className={`event-status status-${status}`}>
              {STATUS_LABEL[status]}
            </span>
            <span className="event-counter">
              {currentIndex + 1} / {filtered.length}
            </span>
          </div>

          <h3 className="event-title">{currentEvent.title}</h3>

          {currentEvent.images && currentEvent.images.length > 0 && (
            <div className="event-images-container">
              <div
                className="event-images-scroll"
                ref={imagesScrollRef}
                onScroll={checkScrollPosition}
              >
                {currentEvent.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`${currentEvent.title} ${index + 1}`}
                    loading="lazy"
                  />
                ))}
              </div>
              {showLeftArrow && (
                <button
                  className="image-scroll-btn left"
                  onClick={() => scrollImages("left")}
                  aria-label="Scroll images left"
                >
                  ❮
                </button>
              )}
              {showRightArrow && (
                <button
                  className="image-scroll-btn right"
                  onClick={() => scrollImages("right")}
                  aria-label="Scroll images right"
                >
                  ❯
                </button>
              )}
            </div>
          )}

          <p className="event-description">{currentEvent.description}</p>

          <div className="event-meta">
            <div className="event-meta-row">
              <span className="event-meta-label">Location</span>
              <span className="event-meta-value">{currentEvent.location}</span>
            </div>
            <div className="event-meta-row">
              <span className="event-meta-label">Start</span>
              <span className="event-meta-value">
                {currentEvent.start || "To be confirmed"}
              </span>
            </div>
            <div className="event-meta-row">
              <span className="event-meta-label">End</span>
              <span className="event-meta-value">
                {currentEvent.end || "To be confirmed"}
              </span>
            </div>
          </div>

          <div className="event-actions">
            {!isPast && currentEvent.registrationUrl && (
              <a
                className="register-btn"
                href={currentEvent.registrationUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Register / Learn more →
              </a>
            )}

            {currentEvent.hasValidDates && !isPast && (
              <div className="calendar-buttons">
                <span className="calendar-buttons-title">Add to calendar</span>
                <div className="calendar-buttons-row">
                  <a
                    href={currentEvent.googleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="calendar-btn google"
                  >
                    Google
                  </a>
                  <a
                    href={currentEvent.outlookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="calendar-btn outlook"
                  >
                    Outlook
                  </a>
                  <a
                    href={currentEvent.yahooUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="calendar-btn yahoo"
                  >
                    Yahoo
                  </a>
                  <button
                    type="button"
                    onClick={() => downloadICS(currentEvent)}
                    className="calendar-btn ics"
                  >
                    Apple / .ics
                  </button>
                </div>
              </div>
            )}

            {!currentEvent.hasValidDates && !isPast && (
              <p className="event-hint">
                Dates for this event are still to be confirmed — check back soon
                or use the registration link for the latest details.
              </p>
            )}
          </div>
        </div>

        <button
          className="slider-btn next"
          onClick={handleNext}
          aria-label="Next event"
          disabled={filtered.length <= 1}
        >
          ❯
        </button>
      </div>
    </div>
  );
};

function FilterTabs({ filter, setFilter }) {
  const tabs = [
    { id: "all", label: "All" },
    { id: "upcoming", label: "Upcoming" },
    { id: "past", label: "Past" },
  ];
  return (
    <div className="event-filter-tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          className={`event-filter-tab ${filter === t.id ? "active" : ""}`}
          onClick={() => setFilter(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export default EventPage;
